import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams, useRouteMatch, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  Avatar,
  ColumnsButton,
  ColumnsDrawer,
  CompanyLink,
  Confirmation,
  CreateButton,
  DateTime,
  ExportDialog,
  ExportDropdown,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
  MemberContactPopover,
  Page,
  SearchInput,
  Tag,
  Tooltip,
  YesNoFilter,
} from '~/components';
import { CompanyFiltersBar, CompanyFiltersGroup, FilterButton, FiltersDrawer } from '~/components/filters';
import { useApi, useConfirmation, useIntegrations, useToast, useWorkspace } from '~/contexts';
import {
  useCompanyFilters,
  useDocumentTitle,
  useIsMounted,
  useSearchParams,
  useSearchParamsConfig,
  useViewColumns,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort, mimeTypes } from '~/utils';
import HubspotIndicator from '../components/HubspotIndicator';
import SalesforceIndicator from '../components/SalesforceIndicator';
import CompanyCloneForm from './CompanyCloneForm';
import CompanyDeleteConfirmation from './CompanyDeleteConfirmation';
import CompanyForm from './CompanyForm';
import CompanyToClientForm from './CompanyToClientForm';

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const CompanyInfo = styled.div`
  line-height: 1;
  align-items: center;
  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const CompanyName = styled.div`
  display: flex;
  align-items: center;
`;

const Owner = styled.div`
  display: flex;
  align-items: center;

  > div {
    margin-left: 0.5rem;
  }
`;

const AssignmentTitle = styled.p`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  margin-left: 0.25rem;
`;

function FirstAssignment({ companyContacts }) {
  const firstContact = companyContacts[0]?.contact;
  if (!firstContact) return null;

  return (
    <Tag style={{ backgroundColor: colors.grey5 }}>
      <small>{firstContact.name}</small>
    </Tag>
  );
}

function Assignments({ companyContacts }) {
  const count = companyContacts.length - 1;
  if (count <= 0) return null;

  return (
    <Tooltip
      message={
        <div style={{ fontSize: '1rem' }}>
          <AssignmentTitle>Contacts</AssignmentTitle>
          {companyContacts.map((companyContact) => (
            <Tag style={{ backgroundColor: colors.grey5 }} key={companyContact.contactId}>
              <small>{companyContact.contact.name}</small>
            </Tag>
          ))}
        </div>
      }>
      <Tag style={{ backgroundColor: colors.grey5, color: colors.grey40 }}>
        <small>+{count}</small>
      </Tag>
    </Tooltip>
  );
}

export default function CompaniesListPage({ sessionKey }) {
  const documentTitle = useDocumentTitle('Companies');

  const { workspace } = useWorkspace();
  const integrations = useIntegrations();
  const api = useApi();
  const history = useHistory();
  const { companyId } = useParams();
  const { url } = useRouteMatch();
  const toast = useToast();
  const confirmation = useConfirmation();
  const isMounted = useIsMounted();
  const location = useLocation();
  const [cloneTarget, setCloneTarget] = useState(null);
  const [companyToClient, setCompanyToClient] = useState(null);

  const searchParamsConfig = useSearchParamsConfig();
  const companyFilters = useCompanyFilters({
    companyIsActive: { default: 'true', ...searchParamsConfig.companyIsActive },
  });

  const [query, setQuery] = useState({
    isReady: false,
    searchParamsStatus: 'pending',
    data: null,
    params: {
      q: '',
      companyHubspotSynced: null,
      companySalesforceSynced: null,
      page: 0,
      size: 25,
      sort: new QuerySort('name', 'asc'),
      ...companyFilters.filters,
    },
    action: 'load',
  });

  const setParams = (params) => {
    setQuery((state) => ({
      ...state,
      action: 'filter',
      params: { ...state.params, ...params, page: 0 },
      searchParamsStatus: 'ready',
    }));
  };

  const loadMore = useCallback(() => {
    setQuery((state) => {
      if (
        state.searchParamsStatus !== 'ready' ||
        state.action !== null ||
        !state.data ||
        state.data.total <= state.data.results.length
      ) {
        return state;
      }

      return {
        ...state,
        params: { ...state.params, page: state.params.page + 1 },
        action: 'load-more',
      };
    });
  }, []);

  const removeItem = (id) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.filter((i) => i.id !== id),
        total: state.data.total - 1,
      },
    }));
  };

  const updateItem = (item) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.map((i) => (i.id === item.id ? { ...i, ...item } : i)),
      },
    }));
  };

  const { action, data, isReady, params, searchParamsStatus } = query;

  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        companyHubspotSynced: { default: null },
        companySalesforceSynced: { default: null },
        sort: { default: new QuerySort('name', 'asc'), ...searchParamsConfig.sort },
        ...companyFilters.searchParamsConfig,
      }),
      [searchParamsConfig, companyFilters.searchParamsConfig],
    ),
    sessionKey,
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      q: params.q,
      companyHubspotSynced:
        integrations?.hubspot && params.companyHubspotSynced ? params.companyHubspotSynced : undefined,
      companySalesforceSynced:
        integrations?.salesforce && params.companySalesforceSynced ? params.companySalesforceSynced : undefined,
      page: params.page,
      size: params.size,
      sort: params.sort,
      ...companyFilters.mapUrlSearchParams(params),
    }),
    [params, integrations?.hubspot, integrations?.salesforce, companyFilters],
  );

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((params) => {
      if (params) setParams(params);
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).companies().get(urlSearchParams);

      if (!isMounted.current) return;

      setQuery((state) => ({
        ...state,
        action: null,
        searchParamsStatus: 'ready',
        data: {
          ...data,
          results: state.action === 'load-more' ? [...state.data.results, ...data.results] : data.results,
          total: data.total,
        },
      }));
    } catch (error) {
      setQuery((state) => ({ ...state, data: { total: 0, results: [] } }));
      toast.error(error.message);
    }
  }, [workspace.id, api, isMounted, urlSearchParams, toast]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);

  const handleFilter = (values) => {
    setParams({ ...values });
    searchParams.set({ ..._.omit(values, 'sort') });
  };

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ sort: querySort });
    searchParams.set({ sort: querySort });
  };

  async function handleSaved(company) {
    const { data } = await api.www.workspaces(workspace.id).companies().get({ ids: company.id });
    updateItem(data[0]);
  }

  async function handleDeleted(company) {
    removeItem(company.id);
  }

  const handleDeleteConfirmation = async (company) => {
    confirmation.prompt((resolve) => (
      <CompanyDeleteConfirmation
        company={company}
        onClose={resolve}
        onDelete={() => {
          handleDeleted(company);
          resolve(true);
        }}
      />
    ));
  };

  const handleEdit = ({ id }) => {
    history.push({
      pathname: `/app/${workspace.key}/pipeline/companies/edit/${id}`,
      search: location.search,
      state: { scrollToTop: false },
    });
  };

  const handleOverview = ({ id }) => {
    history.push(`/app/${workspace.key}/pipeline/companies/${id}/overview`);
  };

  async function handleCloned(company) {
    try {
      toast.success('Company cloned successfully.');
      history.push({
        pathname: `/app/${workspace.key}/pipeline/companies/${company.id}/overview/edit`,
        search: location.search,
        state: { scrollToTop: false },
      });
    } catch ({ message }) {
      toast.error(message);
    }
  }

  function handleClose() {
    history.push({
      pathname: `/app/${workspace.key}/pipeline/companies`,
      search: location.search,
      state: { scrollToTop: false },
    });
    documentTitle.set('Companies');
  }

  const handleExport = async (filename, mimeType) => {
    await confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .companies()
          .export(
            { ...urlSearchParams, size: null },
            {
              headers: { accept: mimeType },
              responseType: 'blob',
            },
          )}
        onClose={resolve}
      />
    ));
  };

  const [dialog, setDialog] = useState(null);

  const columns = useViewColumns({
    storageKey: 'companies_list_columns',
    columns: [
      { key: 'name', name: 'Name', disabled: true },
      { key: 'client', name: 'Client', default: true },
      { key: 'owner', name: 'Relationship Owner', default: true },
      { key: 'contacts', name: 'Primary Contacts', default: true },
      { key: 'createdAt', name: 'Created', default: true },
    ],
  });

  const handleShowColumnsDrawer = () => {
    setDialog(
      <ColumnsDrawer
        value={columns.selected}
        columns={columns.all}
        onClose={() => setDialog(null)}
        onApply={(value) => {
          columns.save(value);
          setDialog(null);
        }}
      />,
    );
  };

  if (!isReady && !data) return <PageLoader />;

  const salesforceEnabled = integrations?.salesforce;
  const hubspotEnabled = integrations?.hubspot;

  return (
    <>
      <Page scrollable>
        <Page.Header>
          <Page.Info>
            <Page.Eyebrow>Pipeline</Page.Eyebrow>
            <Page.Title>Companies</Page.Title>
          </Page.Info>

          <Page.Actions>
            <ExportDropdown>
              {({ setIsOpen }) => (
                <>
                  <ExportDropdown.Item
                    onClick={async () => {
                      await handleExport(`companies.csv`, mimeTypes.csv);
                      setIsOpen(false);
                    }}>
                    Export to CSV
                  </ExportDropdown.Item>

                  <ExportDropdown.Item
                    onClick={async () => {
                      await handleExport(`companies.xlsx`, mimeTypes.xlsx);
                      setIsOpen(false);
                    }}>
                    Export to Excel
                  </ExportDropdown.Item>
                </>
              )}
            </ExportDropdown>

            <ColumnsButton isOutline onClick={handleShowColumnsDrawer} />

            <FilterButton isOutline onClick={showFilters} />

            <Tooltip message="New Company">
              <CreateButton onClick={() => history.push(`${url}/new${location.search}`)} />
            </Tooltip>
          </Page.Actions>
        </Page.Header>

        <>
          <Page.Filters>
            <FiltersBar>
              <SearchInput
                value={params.q}
                placeholder="Search"
                onChange={({ target: { value } }) => handleFilter({ q: value })}
              />

              {hubspotEnabled && (
                <YesNoFilter
                  icon="filter"
                  clearable
                  placeholder="HubSpot Synced"
                  value={params.companyHubspotSynced}
                  onChange={({ target: { value } }) => handleFilter({ companyHubspotSynced: value })}
                />
              )}

              {salesforceEnabled && (
                <YesNoFilter
                  icon="filter"
                  clearable
                  placeholder="Salesforce Synced"
                  value={params.companySalesforceSynced}
                  onChange={({ target: { value } }) => handleFilter({ companySalesforceSynced: value })}
                />
              )}

              <CompanyFiltersBar
                filters={params}
                sticky={['companyOwners', 'companyIsActive']}
                onChange={handleFilter}
              />
            </FiltersBar>
          </Page.Filters>
          <Filters values={params} isOpen={filtersVisible} onApply={handleFilter} onClose={hideFilters} />
        </>

        <Page.ListView>
          <ListView>
            <ListView.Header>
              <ListView.Column sticky minWidth="16rem" name="name" onSort={handleSort} sort={params.sort}>
                Name
              </ListView.Column>

              <ListView.Column minWidth="16rem" isVisible={columns.visible.client}>
                Client
              </ListView.Column>

              <ListView.Column width="20rem" isVisible={columns.visible.owner}>
                Relationship Owner
              </ListView.Column>

              <ListView.Column width="20rem" isVisible={columns.visible.contacts}>
                Primary Contacts
              </ListView.Column>

              <ListView.Column
                width="7rem"
                name="createdAt"
                onSort={handleSort}
                sort={params.sort}
                align="right"
                isVisible={columns.visible.createdAt}>
                Created
              </ListView.Column>

              {salesforceEnabled && (
                <ListView.Column width="4rem" align="left">
                  <SalesforceIndicator variant="grey" />
                </ListView.Column>
              )}

              {hubspotEnabled && (
                <ListView.Column width="4rem" align="left">
                  <HubspotIndicator variant="grey" />
                </ListView.Column>
              )}

              <ListViewActions.Column />
            </ListView.Header>

            <ListView.Body fade={action === 'filter'}>
              {query.data.results.map((company) => {
                const {
                  id,
                  owner,
                  isActive,
                  industry,
                  companyContacts,
                  client,
                  permissions,
                  createdAt,
                  salesforceId,
                  hubspotId,
                } = company;

                async function handleRowClick(company) {
                  handleOverview(company);
                }

                return (
                  <ListView.Row key={id} isDisabled={!isActive} onClick={() => handleRowClick(company)}>
                    <ListView.Cell>
                      <CompanyInfo>
                        <CompanyName>
                          <CompanyLink company={company} onClick={(e) => e.stopPropagation()} />
                        </CompanyName>
                        {industry && <small>{industry.name}</small>}
                      </CompanyInfo>
                    </ListView.Cell>

                    <ListView.Cell>{client?.name}</ListView.Cell>

                    <ListView.Cell>
                      {owner && (
                        <Owner>
                          <MemberContactPopover member={owner} placement="left">
                            <Avatar value={owner} isCircle hasBackground initialsFontSize=".9rem" />
                          </MemberContactPopover>
                          <CompanyInfo>
                            <span>{owner.name}</span>
                            {owner.jobTitle && <small>{owner.jobTitle.name}</small>}
                          </CompanyInfo>
                        </Owner>
                      )}
                    </ListView.Cell>

                    <ListView.Cell>
                      <Tags>
                        <FirstAssignment companyContacts={companyContacts} />
                        <Assignments companyContacts={companyContacts} />
                      </Tags>
                    </ListView.Cell>

                    <ListView.Cell>
                      <DateTime value={createdAt} />
                    </ListView.Cell>

                    {salesforceEnabled && <ListView.Cell>{salesforceId && <SalesforceIndicator />}</ListView.Cell>}

                    {hubspotEnabled && <ListView.Cell>{hubspotId && <HubspotIndicator />}</ListView.Cell>}

                    <ListViewActions>
                      <ListViewActions.Edit onClick={() => handleEdit(company)} />

                      <hr />

                      <ListViewMenu>
                        {({ setIsOpen }) => {
                          const handleAction = (action) => setIsOpen(false) || action;

                          async function handleActiveStatusChange(company, flag) {
                            try {
                              setIsOpen(false);

                              const confirm = await confirmation.prompt((resolve) =>
                                flag ? (
                                  <Confirmation resolve={resolve}>
                                    This action will activate the company. Note that this will not activate any
                                    opportunities within the company. Opportunities must be activated separately.
                                  </Confirmation>
                                ) : (
                                  <Confirmation resolve={resolve}>
                                    Are you sure that you want to deactivate this company? This will also deactivate all
                                    opportunities within the company.
                                  </Confirmation>
                                ),
                              );

                              if (!confirm) return;

                              const { data } = await api.www
                                .workspaces(workspace.id)
                                .companies(company.id)
                                .setActiveStatus(flag);

                              await handleSaved(data);
                            } catch (error) {
                              toast.error(error.message || 'There was a problem updating the company.');
                            }
                          }

                          return (
                            <>
                              <ListViewMenu.Item onClick={() => handleAction(handleOverview(company))}>
                                View
                              </ListViewMenu.Item>

                              <ListViewMenu.Item
                                disabled={!permissions.manage}
                                tooltip={
                                  !permissions.manage ? 'Insufficient permissions to edit this company.' : undefined
                                }
                                onClick={() => handleAction(handleEdit(company))}>
                                Edit
                              </ListViewMenu.Item>

                              <ListViewMenu.Item
                                disabled={!permissions.manage}
                                tooltip={
                                  !permissions.manage ? 'Insufficient permissions to delete this company.' : undefined
                                }
                                onClick={() => handleAction(handleDeleteConfirmation(company))}>
                                Delete
                              </ListViewMenu.Item>

                              <ListViewMenu.Item
                                disabled={!permissions.manage}
                                tooltip={
                                  !permissions.manage ? 'Insufficient permissions to clone this company.' : undefined
                                }
                                onClick={() => handleAction(setCloneTarget(company))}>
                                Clone
                              </ListViewMenu.Item>

                              <ListViewMenu.Item
                                disabled={!permissions.manage}
                                tooltip={
                                  !permissions.manage
                                    ? `Insufficient permissions to ${
                                        company.companyIsActive ? 'deactivate' : 'activate'
                                      } this company.`
                                    : undefined
                                }
                                onClick={() => handleAction(handleActiveStatusChange(company, !isActive))}>
                                {isActive ? 'Deactivate' : 'Activate'}
                              </ListViewMenu.Item>

                              <ListViewMenu.Item
                                disabled={!!company.client || !permissions.manage}
                                tooltip={(() => {
                                  if (!permissions.manage)
                                    return 'Insufficient permissions to create a client from this company.';
                                  if (company.client) return 'This company is already linked to a client.';
                                })()}
                                onClick={() => handleAction(setCompanyToClient(company))}>
                                New Client
                              </ListViewMenu.Item>
                            </>
                          );
                        }}
                      </ListViewMenu>
                    </ListViewActions>
                  </ListView.Row>
                );
              })}

              {query.data.results.length === 0 && <ListView.Empty />}

              {query.data.total > query.data.results.length && (
                <ListView.Loader key={query.data.results.length} onIntersecting={loadMore} />
              )}
            </ListView.Body>

            <ListView.Status total={query.data.total} label="Company" isLoading={!!action} />
          </ListView>
        </Page.ListView>
      </Page>
      {companyId && <CompanyForm onSaved={handleSaved} onClose={handleClose} />}
      {cloneTarget && (
        <CompanyCloneForm target={cloneTarget} onClose={() => setCloneTarget(null)} onSaved={handleCloned} />
      )}
      {companyToClient && (
        <CompanyToClientForm
          company={companyToClient}
          onClose={() => setCompanyToClient(false)}
          onSaved={(client) => history.push(`/app/${workspace.key}/clients/${client.key}/overview/edit`)}
        />
      )}

      {dialog}
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const integrations = useIntegrations();

  const hubspotEnabled = integrations?.hubspot;
  const salesforceEnabled = integrations?.salesforce;

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      {hubspotEnabled && (
        <YesNoFilter
          icon="filter"
          clearable
          placeholder="HubSpot Synced"
          value={filters.companyHubspotSynced}
          onChange={({ target: { value } }) => handleFilter({ companyHubspotSynced: value })}
        />
      )}

      {salesforceEnabled && (
        <YesNoFilter
          icon="filter"
          clearable
          placeholder="Salesforce Synced"
          value={filters.companySalesforceSynced}
          onChange={({ target: { value } }) => handleFilter({ companySalesforceSynced: value })}
        />
      )}

      <CompanyFiltersGroup filters={filters} onChange={handleFilter} />
    </FiltersDrawer>
  );
}
